<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import ContactAPI from 'dashboard/api/contacts';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const STAGES = [
  {
    id: 'exploring',
    labelKey: 'MINISTRY.DISCIPLESHIP.STAGES.EXPLORING',
    color: '#DCE8F5',
    textColor: '#1B2A4A',
  },
  {
    id: 'believing',
    labelKey: 'MINISTRY.DISCIPLESHIP.STAGES.BELIEVING',
    color: '#C9A84C',
    textColor: '#fff',
  },
  {
    id: 'growing',
    labelKey: 'MINISTRY.DISCIPLESHIP.STAGES.GROWING',
    color: '#5689C9',
    textColor: '#fff',
  },
  {
    id: 'serving',
    labelKey: 'MINISTRY.DISCIPLESHIP.STAGES.SERVING',
    color: '#3668B4',
    textColor: '#fff',
  },
  {
    id: 'leading',
    labelKey: 'MINISTRY.DISCIPLESHIP.STAGES.LEADING',
    color: '#1B2A4A',
    textColor: '#fff',
  },
];

const STAGE_IDS = STAGES.map(s => s.id);

const stageContacts = ref({});
const isLoading = ref(false);
const movingId = ref(null);

// Search state — one active search at a time
const searchStageId = ref(null);
const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
let searchTimer = null;

const fetchAllStages = async () => {
  isLoading.value = true;
  try {
    const results = await Promise.all(
      STAGES.map(stage => ContactAPI.get(1, 'name', stage.id))
    );
    results.forEach((res, i) => {
      stageContacts.value[STAGES[i].id] = res.data?.payload || [];
    });
  } catch {
    STAGES.forEach(s => {
      stageContacts.value[s.id] = [];
    });
  } finally {
    isLoading.value = false;
  }
};

const moveContact = async (contact, fromStageId, toStageId) => {
  if (fromStageId === toStageId) return;
  movingId.value = contact.id;
  try {
    const { data } = await ContactAPI.getContactLabels(contact.id);
    const currentLabels = data?.payload || [];
    const baseLabels = currentLabels.filter(l => !STAGE_IDS.includes(l));
    await ContactAPI.updateContactLabels(contact.id, [
      ...baseLabels,
      toStageId,
    ]);
    await fetchAllStages();
  } finally {
    movingId.value = null;
  }
};

const openContact = contact => {
  router.push({
    name: 'contact_page',
    params: { accountId: accountId.value, contactId: contact.id },
  });
};

const onSearchInput = stageId => {
  searchStageId.value = stageId;
  clearTimeout(searchTimer);
  if (!searchQuery.value.trim()) {
    searchResults.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    isSearching.value = true;
    try {
      const { data } = await ContactAPI.search(searchQuery.value.trim());
      searchResults.value = data?.payload || [];
    } finally {
      isSearching.value = false;
    }
  }, 300);
};

const addContactToStage = async (contact, stageId) => {
  const { data } = await ContactAPI.getContactLabels(contact.id);
  const currentLabels = data?.payload || [];
  const baseLabels = currentLabels.filter(l => !STAGE_IDS.includes(l));
  await ContactAPI.updateContactLabels(contact.id, [...baseLabels, stageId]);
  searchQuery.value = '';
  searchResults.value = [];
  searchStageId.value = null;
  await fetchAllStages();
};

const closeSearch = () => {
  searchStageId.value = null;
  searchQuery.value = '';
  searchResults.value = [];
};

const openSearch = stage => {
  searchStageId.value = stage.id;
  searchQuery.value = '';
  searchResults.value = [];
};

const relativeDate = dateStr => {
  if (!dateStr) return 'Never';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 60000);
  if (diff < 1) return 'Just now';
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
};

const getInitials = name => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(p => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

onMounted(fetchAllStages);
</script>

<template>
  <div class="flex flex-col h-full bg-n-background overflow-hidden">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 bg-woot-500 text-white shrink-0"
    >
      <div>
        <h1 class="text-xl font-semibold">
          {{ t('MINISTRY.DISCIPLESHIP.TITLE') }}
        </h1>
        <p class="text-sm text-white/70 mt-0.5">
          {{ t('MINISTRY.DISCIPLESHIP.DESCRIPTION') }}
        </p>
      </div>
      <button
        class="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-sm transition-colors"
        @click="fetchAllStages"
      >
        <span
          class="i-lucide-refresh-cw size-4"
          :class="{ 'animate-spin': isLoading }"
        />
        {{ t('MINISTRY.DISCIPLESHIP.REFRESH') }}
      </button>
    </div>

    <!-- Pipeline board -->
    <div class="flex gap-4 p-4 overflow-x-auto h-full items-start">
      <div
        v-for="stage in STAGES"
        :key="stage.id"
        class="flex flex-col shrink-0 w-64 bg-n-slate-2 rounded-xl overflow-hidden"
      >
        <!-- Stage header -->
        <div
          class="flex items-center justify-between px-4 py-3"
          :style="{ backgroundColor: stage.color }"
        >
          <span
            class="text-sm font-semibold"
            :style="{ color: stage.textColor }"
          >
            {{ t(stage.labelKey) }}
          </span>
          <span
            class="text-xs font-bold px-2 py-0.5 rounded-full bg-black/20"
            :style="{ color: stage.textColor }"
          >
            {{ (stageContacts[stage.id] || []).length }}
          </span>
        </div>

        <!-- Contact cards -->
        <div
          class="flex flex-col gap-2 p-2 overflow-y-auto max-h-[calc(100vh-220px)]"
        >
          <div
            v-if="(stageContacts[stage.id] || []).length === 0"
            class="flex flex-col items-center justify-center py-8 text-center"
          >
            <span class="i-lucide-user size-8 text-n-slate-8 mb-2" />
            <p class="text-xs text-n-slate-9">
              {{ t('MINISTRY.DISCIPLESHIP.EMPTY') }}
            </p>
            <p
              v-if="Object.values(stageContacts).every(c => c.length === 0)"
              class="text-xs text-n-slate-8 mt-1 px-3"
            >
              {{ t('MINISTRY.DISCIPLESHIP.SETUP_HINT') }}
            </p>
          </div>

          <div
            v-for="contact in stageContacts[stage.id] || []"
            :key="contact.id"
            class="bg-white dark:bg-n-slate-3 rounded-lg shadow-sm border border-n-weak p-3 cursor-pointer hover:shadow-md hover:border-woot-300 transition-all group"
            :class="{ 'opacity-50': movingId === contact.id }"
            @click="openContact(contact)"
          >
            <!-- Avatar + name -->
            <div class="flex items-center gap-2 mb-2">
              <div
                class="size-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
                :style="{
                  backgroundColor:
                    stage.color === '#DCE8F5' ? '#1B2A4A' : stage.color,
                }"
              >
                {{ getInitials(contact.name) }}
              </div>
              <div class="min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ contact.name || t('MINISTRY.DISCIPLESHIP.CARD.UNKNOWN') }}
                </p>
                <p class="text-xs text-n-slate-9 truncate">
                  {{ contact.email || contact.phone_number || '—' }}
                </p>
              </div>
            </div>

            <!-- Last active -->
            <p class="text-xs text-n-slate-8">
              {{ t('MINISTRY.DISCIPLESHIP.CARD.LAST_ACTIVE') }}:
              {{ relativeDate(contact.last_activity_at) }}
            </p>

            <!-- Move to stage (visible on hover) -->
            <div
              class="mt-2 pt-2 border-t border-n-weak hidden group-hover:flex gap-1 flex-wrap"
              @click.stop
            >
              <button
                v-for="toStage in STAGES.filter(s => s.id !== stage.id)"
                :key="toStage.id"
                class="text-xs px-2 py-0.5 rounded-full border transition-colors"
                :style="{
                  borderColor: toStage.color,
                  color:
                    toStage.color === '#DCE8F5' ? '#1B2A4A' : toStage.color,
                }"
                :class="{
                  'opacity-50 cursor-not-allowed': movingId === contact.id,
                }"
                :disabled="movingId === contact.id"
                @click="moveContact(contact, stage.id, toStage.id)"
              >
                {{
                  t('MINISTRY.DISCIPLESHIP.MOVE_TO', {
                    label: t(toStage.labelKey),
                  })
                }}
              </button>
            </div>
          </div>

          <!-- Add contact to stage -->
          <div class="relative mt-1">
            <div v-if="searchStageId === stage.id" class="flex flex-col gap-1">
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="t('MINISTRY.DISCIPLESHIP.SEARCH_PLACEHOLDER')"
                class="w-full text-sm px-2 py-1.5 rounded-md border border-n-weak bg-white dark:bg-n-slate-3 focus:outline-none focus:border-woot-500"
                autofocus
                @input="onSearchInput(stage.id)"
                @keydown.escape="closeSearch"
              />
              <div v-if="isSearching" class="text-xs text-n-slate-8 px-1">
                {{ t('MINISTRY.DISCIPLESHIP.SEARCHING') }}
              </div>
              <div
                v-else-if="searchResults.length"
                class="bg-white dark:bg-n-slate-3 rounded-md border border-n-weak shadow-md overflow-hidden"
              >
                <button
                  v-for="result in searchResults.slice(0, 5)"
                  :key="result.id"
                  class="w-full text-left px-3 py-2 hover:bg-n-slate-2 transition-colors"
                  @click="addContactToStage(result, stage.id)"
                >
                  <p class="text-sm font-medium text-n-slate-12 truncate">
                    {{ result.name }}
                  </p>
                  <p class="text-xs text-n-slate-8 truncate">
                    {{ result.email || result.phone_number || '—' }}
                  </p>
                </button>
              </div>
              <button
                class="text-xs text-n-slate-8 hover:text-n-slate-12 self-start px-1"
                @click="closeSearch"
              >
                {{ t('MINISTRY.DISCIPLESHIP.CANCEL') }}
              </button>
            </div>
            <button
              v-else
              class="w-full text-xs text-n-slate-8 hover:text-n-slate-12 py-2 flex items-center gap-1 justify-center rounded-md hover:bg-n-slate-3 transition-colors"
              @click="openSearch(stage)"
            >
              <span class="i-lucide-plus size-3" />
              {{ t('MINISTRY.DISCIPLESHIP.ADD_CONTACT') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
